// Which deliveries this page session has already announced, and which of a poll's items to toast.
// A delivery is keyed on `id:version`: a nudge, message or reminder refreshes an open delivery in
// place with a higher version, and that refresh is news again (contract §3, Delivery items).
// In memory only: a reload starts a new session, and the first poll of a session announces only
// what is fresh, so signing in never bursts a day's backlog of toasts (it is on My tasks).

/** At most this many toasts per poll; more become one summary toast. */
export const MAX_TOASTS_PER_POLL = 3;
/** On a session's first poll, older deliveries are marked seen without a toast. */
export const FIRST_POLL_FRESH_MS = 60_000;

interface Announceable {
  id: string;
  version: number;
  createdAt: string;
}

const seen = new Set<string>();
let polled = false;

export const deliveryKey = (item: Pick<Announceable, 'id' | 'version'>) =>
  `${item.id}:${item.version}`;

export function markSeen(key: string) {
  seen.add(key);
}

export function isSeen(key: string): boolean {
  return seen.has(key);
}

/** Forget the session (specs only). */
export function resetSeen() {
  seen.clear();
  polled = false;
}

export interface ToastPlan<T> {
  /** Announce each of these (at most `MAX_TOASTS_PER_POLL`). */
  show: T[];
  /** More than the cap were new: announce this many with one summary toast instead; else 0. */
  summary: number;
}

/** Decide what one successful poll announces, and mark every item of it seen. `quiet` (the user is
 *  already looking at My tasks) announces nothing. Never announces a key twice. */
export function planToasts<T extends Announceable>(
  items: readonly T[],
  { now, quiet }: { now: number; quiet: boolean }
): ToastPlan<T> {
  const first = !polled;
  polled = true;
  const fresh = items.filter(item => {
    const key = deliveryKey(item);
    if (seen.has(key)) return false;
    seen.add(key);
    if (!first) return true;
    const created = Date.parse(item.createdAt);
    return !Number.isNaN(created) && now - created <= FIRST_POLL_FRESH_MS;
  });
  if (quiet || fresh.length === 0) return { show: [], summary: 0 };
  return fresh.length > MAX_TOASTS_PER_POLL
    ? { show: [], summary: fresh.length }
    : { show: fresh, summary: 0 };
}
